import { Link } from 'react-router'

import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { SkeletonRows } from '@/components/ui/skeleton'
import { formatDate, formatRelativeDate } from '@/lib/formatters'
import type { EndorsementQueueView, InternalEndorsementRow } from '@/models/internalEndorsement'
import { PolicyChangeStatus } from '@/models/policyChange'
import { cn } from '@/utils/cn'
import { changeCountsLabel } from '../../PolicyChanges/policyChangeCopy'
import { EMPTY_QUEUE } from './endorsementCopy'
import { EndorsementStepBadge } from './EndorsementStepBadge'

// Steps whose own badge already says the customer has the next move.
const CUSTOMER_STEPS = [PolicyChangeStatus.NEEDS_INFO, PolicyChangeStatus.QUOTE_READY]

interface EndorsementQueueProps {
  rows: InternalEndorsementRow[]
  isLoading: boolean
  isError: boolean
  view: EndorsementQueueView
  selectedId: string | undefined
}

/** The requests in one view of the queue, oldest submission first; the selected one opens beside it. */
export function EndorsementQueue({ rows, isLoading, isError, view, selectedId }: EndorsementQueueProps) {
  if (isLoading) return <SkeletonRows rows={4} />
  if (isError) {
    return <p className="text-destructive text-sm">Couldn&rsquo;t load the requests. Refresh to try again.</p>
  }
  if (rows.length === 0) {
    return <Card className="px-4 py-6 text-sm text-neutral-600">{EMPTY_QUEUE[view]}</Card>
  }
  return (
    <Card className="gap-0 overflow-hidden py-0">
      <ul className="divide-y divide-neutral-100">
        {rows.map((row) => {
          const selected = row.id === selectedId
          return (
            <li key={row.id}>
              <Link
                to={`/insure/internal/endorsements/${row.id}?view=${view}`}
                aria-current={selected ? 'page' : undefined}
                className={cn(
                  'flex flex-col gap-1.5 border-l-2 border-transparent px-4 py-3 hover:bg-neutral-50',
                  selected && 'border-l-brand-600 bg-brand-100/40'
                )}
              >
                <div className="flex items-baseline justify-between gap-2 text-sm">
                  <span className="truncate font-semibold text-neutral-900">
                    {row.idShort} · {row.org.name}
                  </span>
                  <span className="shrink-0 text-xs text-neutral-500">
                    {formatRelativeDate(row.submittedAt)}
                  </span>
                </div>
                <div className="text-xs text-neutral-600">
                  {changeCountsLabel(row.changeCounts)}
                  {row.earliestEffectiveDate && ` · from ${formatDate(row.earliestEffectiveDate)}`}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <EndorsementStepBadge status={row.status} step={row.step} />
                  {row.waitingOnCustomer && !CUSTOMER_STEPS.includes(row.status) && (
                    <Badge variant="warning">Asked the customer</Badge>
                  )}
                </div>
              </Link>
            </li>
          )
        })}
      </ul>
    </Card>
  )
}
